import { Box } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { Divider } from '@portals/core/src/components/Divider/Divider';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { theme } from '@portals/core/src/themes/immobilien/main';
import { CheckCircleOutline } from '@portals/icons';
import { getFixedT } from 'i18next';
import React from 'react';

import { useIF6Context } from '../../../../../../context';
import { useBankData, useBankDataFromConfig } from '../../../../../../hooks';
import { getExternalFinanceCertificateUrl, isFinanceCertificateEnabled } from '../../../../../../utils';
import { getStyles } from '../EstateContact.styles';

interface EstateContactFormSuccessProps {
  firstName: string;
  lastName: string;
  email: string;
  onClose: () => void;
}

const t = getFixedT(null, 'core-immobilien');

export const EstateContactFormSuccess = ({
  firstName,
  lastName,
  email,
  onClose,
}: EstateContactFormSuccessProps): React.ReactElement => {
  const styles = getStyles(theme);

  const isIF6Scope = useIF6Context();
  const useBankDataHook = isIF6Scope ? useBankDataFromConfig : useBankData;
  const { bankCode } = useBankDataHook();

  return (
    <Box className="contact-success-container" sx={styles.successOuterContainer}>
      <Box sx={styles.successInfoContainer}>
        <CheckCircleOutline htmlColor="green" fontSize="inherit" sx={styles.successIcon} />
        <Box>
          <Typography variant="h4" sx={styles.successInfo}>
            {t('contactForm.contactMessageSent', { firstName, lastName })}
          </Typography>
          <Typography variant="body2" sx={styles.successInfo}>
            {t('contactForm.copyMailSent', { email })}
          </Typography>
        </Box>
      </Box>

      <Button fullWidth onClick={onClose}>
        {t('contactForm.returnToEstate')}
      </Button>

      {isFinanceCertificateEnabled(bankCode) && (
        <>
          <Box sx={styles.dividerContainer}>
            <Divider />
          </Box>
          <Box>
            <Typography variant="h4">{t('detailPage.financeCertificate')}</Typography>
            <Typography variant="body2" sx={styles.financeCertificateDescriptionTypography}>
              {t('detailPage.financeCertificateDescription')}
            </Typography>
            <Box sx={styles.financeButtonLabelContainer}>
              <Button fullWidth component="a" href={getExternalFinanceCertificateUrl(bankCode)}>
                {t('detailPage.financeButtonLabel')}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

EstateContactFormSuccess.displayName = 'EstateContactFormSuccess';
